import { useEffect, useMemo, useState } from 'react';
import { fetchArenaState } from '../lib/callables';
import { averageAnswerMs } from '../lib/team-performance';
import type { ArenaEvent, ContestArchive, PublicQuestion, TeamCode, TeamPublic, TeamState } from '../types/arena';

interface ArenaStatePayload {
  event: ArenaEvent | null;
  team: TeamPublic | null;
  teamResolved: boolean;
  state: TeamState | null;
  question: PublicQuestion | null;
  teams: TeamPublic[];
  teamStates?: Record<string, TeamState>;
  teamCodes?: TeamCode[];
  contestArchives?: ContestArchive[];
}

export function useArenaData(teamId: string | null | undefined, enabled = false) {
  const [event, setEvent] = useState<ArenaEvent | null>(null);
  const [team, setTeam] = useState<TeamPublic | null>(null);
  const [teamResolved, setTeamResolved] = useState(false);
  const [state, setState] = useState<TeamState | null>(null);
  const [question, setQuestion] = useState<PublicQuestion | null>(null);
  const [teams, setTeams] = useState<TeamPublic[]>([]);
  const [teamStates, setTeamStates] = useState<Record<string, TeamState>>({});
  const [teamCodes, setTeamCodes] = useState<TeamCode[]>([]);
  const [contestArchives, setContestArchives] = useState<ContestArchive[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setEvent(null);
      setTeam(null);
      setState(null);
      setQuestion(null);
      setTeams([]);
      setTeamStates({});
      setTeamCodes([]);
      setContestArchives([]);
      setTeamResolved(false);
      return;
    }
    let alive = true;
    const refresh = async () => {
      try {
        const payload = await fetchArenaState<ArenaStatePayload>();
        if (!alive) return;
        setEvent(payload.event);
        setTeams(payload.teams ?? []);
        setTeam(payload.team ?? null);
        setState(payload.state ?? null);
        setQuestion(payload.question ?? null);
        setTeamResolved(payload.teamResolved);
        setTeamStates(payload.teamStates ?? {});
        setTeamCodes(payload.teamCodes ?? []);
        setContestArchives(payload.contestArchives ?? []);
        setError(null);
      } catch (reason) {
        if (alive) setError(reason instanceof Error ? reason.message : 'وضعیت مسابقه دریافت نشد.');
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 1000);
    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [enabled, teamId]);

  const rankedTeams = useMemo(
    () =>
      [...teams].sort(
        (a, b) => b.score - a.score || b.correctCount - a.correctCount || b.hardCorrect - a.hardCorrect || averageAnswerMs(a) - averageAnswerMs(b) || a.name.localeCompare(b.name, 'fa'),
      ),
    [teams],
  );
  const rank = team ? rankedTeams.findIndex((item) => item.id === team.id) + 1 : 0;
  return { event, team, teamResolved, state, question, teams: rankedTeams, teamStates, teamCodes, contestArchives, rank, error };
}
